import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Zap, CheckCircle, XCircle, Users, ArrowRight } from "lucide-react";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkInvitation();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkInvitation = async () => {
    try {
      const response = await axios.get(`${API}/invitations/check/${token}`);
      setInviteInfo(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError("This invitation was not found or has already been used.");
      } else if (error.response?.status === 410) {
        setError("This invitation has expired. Please ask for a new invitation.");
      } else {
        setError("Failed to load invitation details.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = () => {
    // Pass invite token through URL state parameter (more secure than sessionStorage)
    const redirectUrl = `${window.location.origin}/dashboard?invite_token=${encodeURIComponent(token)}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const getRoleDisplay = (role) => {
    const roles = {
      project_manager: { label: "Project Manager", color: "bg-purple-100 text-purple-700" },
      office_admin: { label: "Office Administrator", color: "bg-amber-100 text-amber-700" },
      employee: { label: "Employee", color: "bg-blue-100 text-blue-700" },
      subcontractor: { label: "Subcontractor", color: "bg-green-100 text-green-700" },
    };
    return roles[role] || { label: role, color: "bg-slate-100 text-slate-700" };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => navigate("/login")} variant="outline">
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const roleInfo = getRoleDisplay(inviteInfo?.role);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-indigo-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">You're Invited!</CardTitle>
          <CardDescription className="text-base mt-2">
            You've been invited to join a team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Organization</span>
              <span className="font-semibold text-slate-900">{inviteInfo?.organization}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Your Role</span>
              <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Invited By</span>
              <span className="text-slate-900">{inviteInfo?.invited_by}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Email</span>
              <span className="text-slate-900">{inviteInfo?.email}</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleAcceptInvite} 
              className="w-full h-12"
              data-testid="accept-invite-btn"
            >
              Accept Invitation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/login")} 
              className="w-full"
            >
              Decline
            </Button>
          </div>

          <p className="text-xs text-center text-slate-500">
            By accepting, you'll sign in with Google to create your account.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
